import { type ComponentType, type ReactElement } from 'react';
import type { NextPageWithLayout } from '../pages/_app';

type PageModule = { default: NextPageWithLayout };

/**
 * Adapts a Next.js page module for use as a React Router 7 route during the
 * migration. The page's `getLayout` chain is applied exactly as the Pages
 * Router did via `_app`, so routes keep identical layout nesting before the
 * `getLayout` chains are refactored into nested layout routes (a later step).
 *
 * Returned as an RR7 `lazy` loader so each page is code-split and the route
 * tree can be imported (e.g. for matching) without eagerly importing every
 * page module.
 */
export const lazyPage =
  (importPage: () => Promise<PageModule>) =>
  async (): Promise<{ Component: ComponentType }> => {
    const { default: Page } = await importPage();
    const applyLayout = Page.getLayout ?? ((page: ReactElement) => page);

    const Component = (): ReactElement => <>{applyLayout(<Page />)}</>;
    Component.displayName = `RoutePage(${Page.displayName ?? Page.name ?? 'Anonymous'})`;

    return { Component };
  };
